import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchPublicFeed, fetchPublicWatch, ApiError } from './api';
import { HlsPlayer } from './hls-player';
import { mapWithConcurrency } from './concurrency';
import { PublicWatchPayload } from './types';

type FeedState =
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'throttled' }
  | { kind: 'playing'; name: string; videoIds: string[]; index: number; payload: PublicWatchPayload }
  | { kind: 'ended'; name: string; videoIds: string[] };

const RESOLVE_CONCURRENCY = 8;

/** Resolves every video up front (bounded concurrency, so a long playlist doesn't fire dozens of
 * simultaneous requests against /watch's own throttle) so the queue can show every real title
 * immediately, and so jumping to any already-resolved entry is instant. A per-video failure just
 * marks that entry unavailable — it never aborts the whole playlist. */
async function resolveAll(videoIds: string[], cache: Map<string, PublicWatchPayload | null>): Promise<void> {
  const toFetch = videoIds.filter((id) => id && !cache.has(id));
  await mapWithConcurrency(
    toFetch,
    async (id) => cache.set(id, await fetchPublicWatch(id).catch(() => null)),
    RESOLVE_CONCURRENCY,
  );
}

function findPlayable(videoIds: string[], cache: Map<string, PublicWatchPayload | null>, from: number, step: 1 | -1): number {
  for (let i = from; i >= 0 && i < videoIds.length; i += step) {
    const id = videoIds[i];
    if (id && cache.get(id)) return i;
  }
  return -1;
}

export function FeedPage() {
  const { playlistId } = useParams<{ playlistId: string }>();
  const [state, setState] = useState<FeedState>({ kind: 'loading' });
  const cacheRef = useRef(new Map<string, PublicWatchPayload | null>());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!playlistId) {
        setState({ kind: 'unavailable' });
        return;
      }
      try {
        const feed = await fetchPublicFeed(playlistId);
        if (cancelled) return;
        if (!feed || feed.videoIds.length === 0) {
          setState({ kind: 'unavailable' });
          return;
        }
        await resolveAll(feed.videoIds, cacheRef.current);
        if (cancelled) return;
        const firstIndex = findPlayable(feed.videoIds, cacheRef.current, 0, 1);
        if (firstIndex === -1) {
          // Real playlist, but every video in it is currently unplayable — indistinguishable
          // from an unknown playlist link, per the same rule /watch already follows.
          setState({ kind: 'unavailable' });
          return;
        }
        const payload = cacheRef.current.get(feed.videoIds[firstIndex]!)!;
        setState({ kind: 'playing', name: feed.name, videoIds: feed.videoIds, index: firstIndex, payload });
      } catch (e) {
        if (cancelled) return;
        setState(e instanceof ApiError && e.status === 429 ? { kind: 'throttled' } : { kind: 'unavailable' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [playlistId]);

  function playIndex(name: string, videoIds: string[], index: number) {
    const id = videoIds[index];
    const payload = id ? cacheRef.current.get(id) : null;
    if (!payload) return;
    setState({ kind: 'playing', name, videoIds, index, payload });
  }

  function goTo(step: 1 | -1) {
    if (state.kind !== 'playing') return;
    const { name, videoIds, index } = state;
    const next = findPlayable(videoIds, cacheRef.current, index + step, step);
    if (next === -1) {
      if (step === 1) setState({ kind: 'ended', name, videoIds });
      return;
    }
    playIndex(name, videoIds, next);
  }

  const handleSelect = (index: number) => {
    if (state.kind !== 'playing' && state.kind !== 'ended') return;
    playIndex(state.name, state.videoIds, index);
  };

  return (
    <div className="app-shell">
      <header className="app-header">
        <strong>VOD Management</strong>
      </header>
      {state.kind === 'loading' && <p className="muted">Loading…</p>}
      {state.kind === 'unavailable' && (
        <div className="card" style={{ textAlign: 'center' }}>
          <h1>This playlist is not available</h1>
          <p className="muted">
            The link may be invalid, the playlist may have been removed, or it may have no videos
            available to play right now.
          </p>
        </div>
      )}
      {state.kind === 'throttled' && (
        <div className="card" style={{ textAlign: 'center' }}>
          <h1>Please try again later</h1>
          <p className="muted">This page is busy. Wait a moment and reload.</p>
        </div>
      )}
      {(state.kind === 'playing' || state.kind === 'ended') && (
        <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
          <div style={{ flex: '2 1 480px', minWidth: 0 }}>
            <h1 style={{ marginBottom: 'var(--space-3)' }}>{state.name || 'Playlist'}</h1>
            {state.kind === 'playing' ? (
              <>
                <HlsPlayer
                  src={state.payload.hlsUrl}
                  captionUrl={state.payload.captionUrl}
                  autoPlay
                  onEnded={() => goTo(1)}
                />
                <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                  <button
                    className="secondary"
                    disabled={findPlayable(state.videoIds, cacheRef.current, state.index - 1, -1) === -1}
                    onClick={() => goTo(-1)}
                  >
                    ◀ Previous
                  </button>
                  <button
                    className="secondary"
                    disabled={findPlayable(state.videoIds, cacheRef.current, state.index + 1, 1) === -1}
                    onClick={() => goTo(1)}
                  >
                    Next ▶
                  </button>
                </div>
              </>
            ) : (
              <div className="card" style={{ textAlign: 'center' }}>
                <h2>End of playlist</h2>
                <p className="muted">That was the last video in this playlist.</p>
              </div>
            )}
          </div>
          <FeedQueue
            videoIds={state.videoIds}
            currentIndex={state.kind === 'playing' ? state.index : -1}
            cache={cacheRef.current}
            onSelect={handleSelect}
          />
        </div>
      )}
    </div>
  );
}

function FeedQueue({
  videoIds,
  currentIndex,
  cache,
  onSelect,
}: {
  videoIds: string[];
  currentIndex: number;
  cache: Map<string, PublicWatchPayload | null>;
  onSelect: (index: number) => void;
}) {
  return (
    <ol style={{ flex: '1 1 240px', minWidth: 220, listStyle: 'none', padding: 0, margin: 0 }}>
      {videoIds.map((id, index) => {
        const resolved = cache.get(id);
        const label = resolved === null ? 'Unavailable' : resolved?.filename || `Video ${index + 1}`;
        const current = index === currentIndex;
        return (
          <li key={`${id}-${index}`} style={{ marginBottom: 'var(--space-2)' }}>
            <button
              className={current ? undefined : 'secondary'}
              disabled={!resolved}
              onClick={() => onSelect(index)}
              style={{
                width: '100%',
                textAlign: 'left',
                fontWeight: current ? 'bold' : undefined,
                opacity: resolved ? 1 : 0.6,
              }}
            >
              {index + 1}. {label}
            </button>
          </li>
        );
      })}
    </ol>
  );
}
